import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, CircularProgress } from '@mui/material';
import { useRef, useState } from 'react';
import { GobikesApi } from '../../config/Apis';

type Mode = 'forgotUsername' | 'forgotPassword';
type Step = 'input' | 'otp' | 'success';

interface Props {
  open: boolean;
  mode: Mode;
  onClose: () => void;
}

function ForgotCredentials({ open, mode, onClose }: Props) {

  const [step, setStep] = useState<Step>('input');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoveredAccounts, setRecoveredAccounts] = useState<{ username: string; city: string }[]>([]);
  // Saved after step 1 so they're available after input fields unmount
  const [savedPhone, setSavedPhone] = useState('');
  const [savedUsername, setSavedUsername] = useState('');

  const phoneRef = useRef<HTMLInputElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  function handleClose() {
    setStep('input');
    setError('');
    setRecoveredAccounts([]);
    setSavedPhone('');
    setSavedUsername('');
    onClose();
  }

  async function handleSendOtp() {
    setError('');
    const phone = phoneRef.current?.value?.trim();
    if (!phone) { setError('Please enter your mobile number'); return; }

    if (mode === 'forgotPassword') {
      const username = usernameRef.current?.value?.trim();
      if (!username) { setError('Please enter your username'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'forgotUsername') {
        await GobikesApi.post('/vendors/forgot-username', { phone });
      } else {
        const username = usernameRef.current?.value?.trim() ?? '';
        await GobikesApi.post('/vendors/forgot-password', { username, phone });
        setSavedUsername(username);
      }
      setSavedPhone(phone);
      setStep('otp');
    } catch (e: any) {
      setError((e.response?.data?.message) ? e.response.data.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    setError('');
    const otp = otpRef.current?.value?.trim();
    if (!otp) { setError('Please enter the OTP'); return; }

    if (mode === 'forgotPassword') {
      const newPassword = newPasswordRef.current?.value;
      const confirmPassword = confirmPasswordRef.current?.value;
      if (!newPassword) { setError('Please enter a new password'); return; }
      if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'forgotUsername') {
        const { data } = await GobikesApi.post('/vendors/verify-forgot-username', { phone: savedPhone, otp });
        setRecoveredAccounts(data.data.accounts);
      } else {
        const newPassword = newPasswordRef.current?.value;
        await GobikesApi.post('/vendors/reset-password', { username: savedUsername, phone: savedPhone, otp, newPassword });
      }
      setStep('success');
    } catch (e: any) {
      setError((e.response?.data?.message) ? e.response.data.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  const title = mode === 'forgotUsername' ? 'Recover Username' : 'Reset Password';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>

        {step === 'input' && (
          <>
            {mode === 'forgotPassword' && (
              <TextField inputRef={usernameRef} label="Username" variant="outlined" fullWidth />
            )}
            <TextField inputRef={phoneRef} label="Mobile Number" variant="outlined" fullWidth />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </>
        )}

        {step === 'otp' && (
          <>
            <Typography variant="body2" color="text.secondary">
              An OTP has been sent to your registered mobile number.
            </Typography>
            <TextField inputRef={otpRef} label="OTP" variant="outlined" fullWidth inputProps={{ maxLength: 4 }} />
            {mode === 'forgotPassword' && (
              <>
                <TextField inputRef={newPasswordRef} label="New Password" type="password" variant="outlined" fullWidth />
                <TextField inputRef={confirmPasswordRef} label="Confirm Password" type="password" variant="outlined" fullWidth />
              </>
            )}
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </>
        )}

        {step === 'success' && mode === 'forgotUsername' && (
          <>
            <Typography variant="body2">
              {recoveredAccounts.length === 1 ? 'Your username is:' : 'Accounts associated with this number:'}
            </Typography>
            {recoveredAccounts.map((a, i) => (
              <Typography key={i} variant="h6" fontWeight={700}>
                {a.username} ({a.city})
              </Typography>
            ))}
          </>
        )}

        {step === 'success' && mode === 'forgotPassword' && (
          <Typography variant="body2">Your password has been reset successfully. You can now log in with your new password.</Typography>
        )}

      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'input' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSendOtp} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Send OTP'}
            </Button>
          </>
        )}
        {step === 'otp' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleVerifyOtp} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : (mode === 'forgotUsername' ? 'Verify' : 'Reset Password')}
            </Button>
          </>
        )}
        {step === 'success' && (
          <Button variant="contained" onClick={handleClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ForgotCredentials;
